import React, { useReducer, useContext, useEffect, useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link } from 'react-router-dom';
import { PermissionsContext } from '../../UIContainer/PermissionsWrapper';
import PermissionConstants from '../../../Common/Constants/PermissionConstants';
import { sqlServerService } from '../SqlServerService';
import { MenuType } from '../Model/SqlServerModel';

type MenuItemType = {
  id: string;
  label: string;
  getLink: (server: string) => string;
  validation: MenuType;
};

const menuItems: MenuItemType[] = [
  {
    id: 'edit',
    label: 'Edit',
    getLink: (server) => `/sqlserver/${server}/edit`,
    validation: MenuType.Edit,
  },
  {
    id: 'delete',
    label: 'Delete',
    getLink: (server) => `/sqlserver/${server}/delete`,
    validation: MenuType.Delete,
  },
];

type StateType = {
  anchor: HTMLButtonElement | null;
  menuOpen: boolean;
};

const initialState: StateType = {
  anchor: null,
  menuOpen: false,
};

type ActionType =
  | { type: 'OPEN_MENU'; payload: HTMLButtonElement }
  | { type: 'CLOSE_MENU' };

function reducer(state: StateType, action: ActionType): StateType {
  switch (action.type) {
    case 'OPEN_MENU':
      return { ...state, menuOpen: true, anchor: action.payload };
    case 'CLOSE_MENU':
      return { ...state, menuOpen: false, anchor: null };
    default:
      return state;
  }
}

type SqlServerMenuProps = {
  server: string;
};

export default function SqlServerMenu({
  server,
}: Readonly<SqlServerMenuProps>) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [canDeleteServer, setCanDeleteServer] = useState(false);
  const permissions = useContext(PermissionsContext);

  useEffect(() => {
    const checkServerBinding = async () => {
      try {
        const response = await sqlServerService.canDeleteSqlServer(server);
        setCanDeleteServer(response);
      } catch (error) {
        console.error('Error fetching server binding status:', error);
      }
    };

    checkServerBinding();
  }, [server]);

  const hasPermission = (type: MenuType): boolean => {
    if (type === MenuType.Delete) {
      return (
        !!permissions[PermissionConstants.SqlServerWriter] && canDeleteServer
      );
    }
    return !!permissions[PermissionConstants.SqlServerWriter];
  };

  const validMenuItems = menuItems.filter((menu) =>
    hasPermission(menu.validation),
  );

  return (
    <div>
      <IconButton
        onClick={(event) =>
          dispatch({ type: 'OPEN_MENU', payload: event.currentTarget })
        }
        size="large"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={state.anchor}
        open={state.menuOpen}
        onClose={() => dispatch({ type: 'CLOSE_MENU' })}
      >
        {validMenuItems.map((menu) => (
          <MenuItem key={menu.id} component={Link} to={menu.getLink(server)}>
            {menu.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
